/**
 * Service-provider specialties.
 *
 * Stored on `ServiceProvider.specialty` as free-text (STRING, not ENUM)
 * so adding a new specialty doesn't require a DB migration. The picker
 * surfaces this curated list for consistency, but users can pick
 * "Other" and type their own — both shapes round-trip through the same
 * column.
 *
 * `value` is what gets persisted; `label` is what gets displayed. Keep
 * `value` lowercase + underscored so a future migration to a real enum
 * is a 1:1 rename.
 */

export interface ServiceProviderSpecialty {
    value: string;
    label: string;
}

export const SERVICE_PROVIDER_SPECIALTY_OTHER = 'other';

export const SERVICE_PROVIDER_SPECIALTIES: ServiceProviderSpecialty[] = [
    { value: 'promoter', label: 'Promoter' },
    { value: 'booking_agent', label: 'Booking Agent' },
    { value: 'photographer', label: 'Photographer' },
    { value: 'videographer', label: 'Videographer' },
    { value: 'sound_engineer', label: 'Sound Engineer' },
    { value: 'lighting_designer', label: 'Lighting Designer' },
    { value: 'stage_manager', label: 'Stage Manager' },
    { value: 'production_manager', label: 'Production Manager' },
    { value: 'tour_manager', label: 'Tour Manager' },
    { value: 'caterer', label: 'Caterer' },
    { value: 'security', label: 'Security' },
    { value: 'hospitality', label: 'Hospitality' },
    { value: SERVICE_PROVIDER_SPECIALTY_OTHER, label: 'Other' },
];

const SPECIALTY_LABEL_BY_VALUE = new Map(
    SERVICE_PROVIDER_SPECIALTIES.map((s) => [s.value, s.label]),
);

/**
 * Map a stored specialty string to its display label.
 * - Known curated values map to their label
 * - 'other' / null / undefined → 'Collaborator'
 * - Unknown free-text → returned as-is, capitalized first letter
 */
export function getSpecialtyLabel(specialty?: string | null): string {
    if (!specialty) return 'Collaborator';
    const trimmed = specialty.trim().toLowerCase();
    if (!trimmed || trimmed === SERVICE_PROVIDER_SPECIALTY_OTHER) return 'Collaborator';
    const known = SPECIALTY_LABEL_BY_VALUE.get(trimmed);
    if (known) return known;
    // Free-text fallback: capitalize first letter, leave the rest alone
    // so users who typed "Front-of-house lead" don't get title-cased.
    return specialty.charAt(0).toUpperCase() + specialty.slice(1);
}

/**
 * Display label for a workspace entity. Replaces raw `entity.type` in
 * UI surfaces (the switcher most importantly) — service providers get
 * their specialty rather than the generic "Service Provider".
 */
export interface DisplayableEntity {
    type: string;
    /** Optional joined ServiceProvider profile carrying the specialty. */
    managedServiceProvider?: Array<{ specialty?: string | null }> | null;
}

export function getEntityTypeDisplay(entity: DisplayableEntity | null | undefined): string {
    if (!entity) return '';
    if (entity.type === 'venue') return 'Venue';
    if (entity.type === 'performer') return 'Performer';
    if (entity.type === 'service_provider') {
        const specialty = entity.managedServiceProvider?.[0]?.specialty;
        return getSpecialtyLabel(specialty);
    }
    // Unknown future types — capitalize and replace underscores.
    return entity.type
        .split('_')
        .map((s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s))
        .join(' ');
}
